import { FieldModel, ResourceDetail, TraitId } from '@cibo/core'
import { ExpansionPanel, FriendlyError } from '@cibo/ui'
import LockIcon from '@mui/icons-material/Lock'
import { Button, Skeleton, Stack, Typography } from '@mui/material'
import { ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useDetailEditing } from '../../features'
import { useLandReportView } from './LandReportViewProvider'

interface DetailPanelProps<T> {
  title: string
  traitId: TraitId
  DetailTable?: (p: { field: FieldModel; focusYear: number }) => ReactElement
  DetailIndividual: (p: { currentYearData?: T }) => ReactElement
  expandable?: boolean
  testId?: string
}

export function DetailPanel<T extends ResourceDetail>({
  DetailTable,
  DetailIndividual,
  title,
  traitId,
  expandable = true,
  testId,
}: DetailPanelProps<T>) {
  const { resourceId } = useParams()
  const { field, focusYear } = useLandReportView({ resourceId })
  const { t } = useTranslation('@cibo/landmanager/LandReport')
  const { setDetailModal } = useDetailEditing()

  if (testId) {
    // @ts-ignore while we could type otherProps appropriately, using LoadingButtonProps from mui/lab is mysteriously catastrophic.
    otherProps['data-testid'] = testId
  }

  const currentYearData = useMemo(() => {
    if (!field.data) {
      return undefined
    }
    if (!expandable) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return field.data!.traitDetails<T>(traitId)[0] as T
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return (field.data!.traitDetails<T>(traitId) as T[]).find(c => c.year === focusYear)
  }, [field.data, focusYear])

  const handleEdit = () => {
    if (!!resourceId) {
      if (!expandable) {
        setDetailModal({
          resourceId,
          traitId,
          requirement: { traitId, dataType: 'field' },
        })
      } else {
        setDetailModal({
          resourceId,
          traitId,
          requirement: { traitId, year: focusYear, dataType: 'field' },
        })
      }
    }
  }

  return (
    <Stack spacing={1} flex={1} data-testid={`detailPanel-${traitId}`}>
      <ExpansionPanel
        spacing={1}
        header={<Typography variant="subtitle1">{title}</Typography>}
        collapsed={
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={3} alignItems="center">
              {expandable && <Typography variant="subtitle1">{focusYear}</Typography>}
              {currentYearData?.immutable && <LockIcon color="disabled" />}
              {!!field.isLoading ? (
                <Stack>
                  <Skeleton>
                    <Typography variant="subtitle1">foobar</Typography>
                  </Skeleton>
                </Stack>
              ) : field.isError ? (
                <FriendlyError message={t('genericError')} />
              ) : (
                <DetailIndividual currentYearData={currentYearData} />
              )}
            </Stack>

            <Button
              variant="outlined"
              disabled={
                field.isLoading || !field.data?.can(['edit_meta']) || currentYearData?.immutable
              }
              onClick={handleEdit}
              size="small"
            >
              {!!currentYearData ? t('edit') : t('add')}
            </Button>
          </Stack>
        }
        expanded={
          expandable &&
          DetailTable &&
          !!field.data && <DetailTable field={field.data} focusYear={focusYear} />
        }
        disabled={field.isLoading}
      />
    </Stack>
  )
}
